/**
 * <SnippetHtml/> — fetch a server-rendered snippet from
 * ``/api/snippets/{scope}/{name}?…params`` and drop the HTML into the
 * DOM.
 *
 * Rationale:
 *  - Every module can render the same POI card, address block, hours
 *    badge, breadcrumbs, CTA button — from the same server source.
 *  - Any styling / structure change ships instantly across every
 *    consumer (React app, WP plugin, embedded partner sites) with no
 *    frontend rebuild.
 *  - Aligns with the platform's Snippet-First architecture: the
 *    Python snippet folder is the single source of truth.
 *
 * Usage:
 *  <SnippetHtml scope="global" name="address_block" params={{poi_id}} />
 *  <SnippetHtml scope="global" name="open_hours"
 *               params={{opening_hours: meta.opening_hours}} />
 *
 * Notes:
 *  - Uses ``dangerouslySetInnerHTML`` — snippet HTML is server-owned
 *    and trusted. Never pass user input through the params.
 *  - Skeleton fallback while loading; hides completely on empty HTML
 *    so callers can drop it in even when the underlying data is
 *    missing (e.g. no phone → nothing renders).
 */
import React, { useEffect, useState, useMemo } from "react";

const API = process.env.REACT_APP_BACKEND_URL || "";

function toQuery(params) {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params || {})) {
    if (v === undefined || v === null || v === "") continue;
    q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : "";
}

export function SnippetHtml({ scope, name, params, className }) {
  const [html, setHtml] = useState("");
  const [state, setState] = useState("loading"); // loading | ready | error | empty
  const url = useMemo(
    () => `${API}/api/snippets/${scope}/${name}${toQuery(params)}`,
    [scope, name, params],
  );

  useEffect(() => {
    let dead = false;
    setState("loading");
    // 6-second timeout: better to show empty than a shimmer bar forever
    // if the snippet endpoint hangs (rare, but happens when the backend
    // is under load or a downstream cache is warming).
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 6000);
    fetch(url, { credentials: "omit", signal: ctrl.signal })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then((body) => {
        if (dead) return;
        const trimmed = (body || "").trim();
        setHtml(trimmed);
        setState(trimmed ? "ready" : "empty");
      })
      .catch(() => {
        if (dead) return;
        setState("empty");
      })
      .finally(() => clearTimeout(timer));
    return () => { dead = true; clearTimeout(timer); ctrl.abort(); };
  }, [url]);

  if (state === "empty") return null;
  if (state === "error") return null;
  if (state === "loading") {
    return (
      <div
        className={`snippet-skel ${className || ""}`}
        data-testid={`snippet-loading-${scope}-${name}`}
      />
    );
  }
  return (
    <div
      className={`snippet snippet-${scope}-${name} ${className || ""}`}
      data-testid={`snippet-${scope}-${name}`}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
